import router from '@builder/router/index'
import shortid from 'shortid'

const computedProperty = [
  'width', 'height', 'font-size', 'max-width', 'max-height', 'min-width', 'min-height'
]

const normalizeMediaText = (mt = '') => {
  return String(mt || '')
    .toLowerCase()
    .replace(/\s+/g, '')
}

const isNegativeZIndexValue = (value) => {
  const raw = String(value ?? '')
    .replace(/\s*!important\s*$/i, '')
    .trim()

  if (!raw) return false

  const zNum = Number(raw)
  if (!Number.isNaN(zNum)) return zNum < 0

  if (raw.startsWith('-')) return true
  if (/^calc\(\s*-/i.test(raw)) return true

  const varFallback = raw.match(/^var\(\s*--[A-Za-z0-9_-]+\s*,\s*([^)]+)\)$/i)
  if (varFallback?.[1]) {
    return isNegativeZIndexValue(varFallback[1])
  }

  return false
}

const sanitizeNegativeZIndexText = (cssText = '') => {
  return String(cssText || '')
    .replace(/z-index\s*:\s*([^;}]*)/gi, (m, v) => {
      return isNegativeZIndexValue(v) ? '' : m
    })
    .replace(/;\s*;/g, ';')
    .replace(/\{\s*;+/g, '{')
    .replace(/;\s*\}/g, '}')
}

const sanitizeNegativeZIndex = (cssRule) => {
  const cssText = cssRule?.cssText || ''

  const isStyleRule = cssRule && (cssRule.type === CSSRule.STYLE_RULE)
  if (!isStyleRule) return cssText

  try {
    const style = cssRule.style
    if (style) {
      const rawZ = style.getPropertyValue('z-index')
      if (rawZ) {
        if (isNegativeZIndexValue(rawZ)) {
          let decls = ''
          for (let i = 0; i < style.length; i++) {
            const prop = style[i]
            if (!prop) continue
            if (prop === 'z-index') continue

            const val = style.getPropertyValue(prop)
            const prio = style.getPropertyPriority(prop)
            if (val == null) continue

            decls += `${prop}:${String(val).trim()}${prio ? ' !important' : ''};`
          }

          const sel = cssRule.selectorText || ''
          return `${sel}{${decls}}`
        }
      }
    }
  } catch (e) {
    // ignore
  }

  return sanitizeNegativeZIndexText(cssText)
}

const getStylesheetEl = () => {
  return router.currentRoute.name === 'editor' ? document.getElementById('sty') : document.getElementById('subject-styles')
}

const getVarValue = (sid, varName, pseudo = null, defaultValue = 0) => {
  const $el = document.querySelector(sid)
  return window.getComputedStyle($el, pseudo || '').getPropertyValue(varName) || defaultValue
}

const retriveStyles = (css, css_hash) => {
  const $styleEl = document.createElement('style')
  document.head.appendChild($styleEl)
  $styleEl.textContent = css || ''

  const styles = [[], [], []]
  const wantedMedia = ['screen', '(max-width: 1100px)', '(max-width: 520px)']
  const wantedMediaNorm = wantedMedia.map(normalizeMediaText)
  const mediaToIndex = new Map(wantedMediaNorm.map((m, i) => [m, i]))

  const sheetRules = [...($styleEl.sheet?.cssRules || [])]

  const pushIfMatch = (idx, cssRule) => {
    const sel = cssRule?.selectorText
    if (typeof sel === 'string' && sel.includes(css_hash)) {
      styles[idx].push(sanitizeNegativeZIndex(cssRule))
    }
  }

  sheetRules.forEach(rule => {
    // bare rules => screen bucket
    if (rule?.type === CSSRule.STYLE_RULE) {
      pushIfMatch(0, rule)
      return
    }

    if (rule?.type === CSSRule.MEDIA_RULE) {
      const raw = rule.media?.mediaText || ''
      const norm = normalizeMediaText(raw)
      const idx = mediaToIndex.get(norm === 'all' || norm === '' ? wantedMediaNorm[0] : norm)
      if (idx === undefined) return

      for (let j = 0; j < rule.cssRules.length; j++) {
        const inner = rule.cssRules[j]
        if (inner?.type === CSSRule.STYLE_RULE) pushIfMatch(idx, inner)
      }
    }
  })

  $styleEl.remove()
  return styles
}

const retriveAllStyles = (css) => {
  const $styleEl = document.createElement('style')
  document.head.appendChild($styleEl)
  $styleEl.textContent = css || ''

  const styles = [[], [], []]

  const canonicalBucket = (mt = '') => {
    const norm = normalizeMediaText(mt)
    if (!norm || norm === 'all' || norm === 'screen') return 0
    if (norm.includes('max-width:1100px')) return 1
    if (norm.includes('max-width:520px')) return 2
    return null
  }

  const sheetRules = [...($styleEl.sheet?.cssRules || [])]

  let mediaPos = 0

  const pushRule = (idx, cssRule) => {
    if (idx == null) return
    if (!cssRule) return

    if (cssRule.type === CSSRule.STYLE_RULE) {
      styles[idx].push(sanitizeNegativeZIndex(cssRule))
      return
    }

    if (cssRule?.cssText) styles[idx].push(cssRule.cssText)
  }

  for (const rule of sheetRules) {
    if (rule?.type === CSSRule.STYLE_RULE) {
      pushRule(0, rule)
      continue
    }

    if (rule?.type === CSSRule.MEDIA_RULE) {
      const idxByPos = mediaPos < 3 ? mediaPos : null
      mediaPos++

      const idx = idxByPos != null ? idxByPos : canonicalBucket(rule.media?.mediaText || '')
      if (idx == null) continue

      for (let j = 0; j < (rule.cssRules?.length || 0); j++) {
        const inner = rule.cssRules[j]
        if (inner?.type === CSSRule.STYLE_RULE) {
          pushRule(idx, inner)
        } else if (inner?.cssText) {
          pushRule(idx, inner)
        }
      }
    }
  }

  $styleEl.remove()
  return styles
}

const getPageStyles = (css_hash) => {
  const sheet = getStylesheetEl()?.sheet
  const topRules = [...(sheet?.cssRules || [])]

  const styles = [[], [], []]
  const wantedMedia = ['screen', '(max-width: 1100px)', '(max-width: 520px)']
  const wantedMediaNorm = wantedMedia.map(normalizeMediaText)
  const mediaToIndex = new Map(wantedMediaNorm.map((m, i) => [m, i]))

  topRules.forEach(rule => {
    if (!rule || rule.type !== CSSRule.MEDIA_RULE) return

    const raw = rule.media?.mediaText || ''
    const norm = normalizeMediaText(raw)
    const idx = mediaToIndex.get(norm === 'all' || norm === '' ? wantedMediaNorm[0] : norm)
    if (idx === undefined) return

    let j = 0
    while (j < rule.cssRules.length) {
      const inner = rule.cssRules[j]
      const sel = inner?.selectorText

      if (typeof sel === 'string' && sel.includes(css_hash)) {
        const cssText = inner.cssText

        try {
          // deleteRule exists on CSSMediaRule
          rule.deleteRule(j)
        } catch (e) {
          styles[idx].push(cssText)
          j++
          continue
        }

        styles[idx].push(cssText)
        continue // IMPORTANT: don’t j++ after delete
      }

      j++
    }
  })

  return styles
}

const pageStylesToString = (selector = 'sty') => {
  const $styEl = document.querySelector(`#${selector}`)
  if (!$styEl || !$styEl.sheet) return ''

  const $cloneStyle = document.createElement('style')
  document.head.appendChild($cloneStyle)

  const cssRules = [...$styEl.sheet.cssRules]
  $cloneStyle.textContent = cssRules.map(r => r.cssText).join(' ')

  let output = ''
  try {
    output = [...($cloneStyle.sheet?.cssRules || [])].reduce((acc, rule) => {
      if (rule?.type === CSSRule.MEDIA_RULE) {
        clearEmptyAndDuplicateRules(rule)
      }
      return acc + rule.cssText
    }, '')
  } catch (e) {
    output = $cloneStyle.textContent || ''
  }

  $cloneStyle.remove()
  return output
}

const pageStylesToStringAsync = async (selector = 'sty') => {
  const $styEl = document.querySelector(`#${selector}`)
  if (!$styEl || !$styEl.sheet) return ''

  const $cloneStyle = document.createElement('style')
  document.head.appendChild($cloneStyle)

  const cssRules = [...$styEl.sheet.cssRules]
  $cloneStyle.textContent = cssRules.map(r => r.cssText).join(' ')

  let output = ''
  try {
    output = [...($cloneStyle.sheet?.cssRules || [])].reduce((acc, rule) => {
      if (rule?.type === CSSRule.MEDIA_RULE) {
        clearEmptyAndDuplicateRules(rule)
      }
      return acc + rule.cssText
    }, '')
  } catch (e) {
    output = $cloneStyle.textContent || ''
  }

  $cloneStyle.remove()
  return output
}

const origPageStylesToString = (selector = 'sty') => {
  const $styEl = document.querySelector(`#${selector}`)


  const output = [...$styEl.sheet.cssRules].reduce((accumulator, currentValue) => {
    clearEmptyAndDuplicateRules(currentValue)
    return accumulator += currentValue.cssText
  }, '')

  return output
}

const getOriginalComponentPropertyValue = (selector, property, pseudo = '') => {
  if (!computedProperty.includes(property)) return ''

  const $componentsStyleEl = document.getElementById('components-style')
  const selectorName = `${selector}${pseudo}`

  let j = 0, len = [...$componentsStyleEl.sheet.cssRules].length
  while (j < len) {
    const selector = $componentsStyleEl.sheet.cssRules[j].selectorText
    if (selector && selector.replaceAll('\\', '') === selectorName) {
      return $componentsStyleEl.sheet.cssRules[j].style.getPropertyValue(property)
    }

    j++
  }
}

const deleteStylesBySid = (sid) => {
  const styleRules = [...getStylesheetEl().sheet.cssRules]

  for (let i = 0; i < styleRules.length; i++) {
    const styleSheet = styleRules[i]
    try {
      const rules = styleSheet.cssRules || styleSheet.rules
      for (let j = rules.length - 1; j >= 0; j--) {
        const rule = rules[j]
        if (rule.selectorText && rule.selectorText.indexOf(sid) !== -1) {
          styleSheet.deleteRule(j)
        }
      }
    } catch (e) {
      // Catch possible cross-origin stylesheet access errors
      console.error('Error accessing stylesheet:', e)
    }
  }
}

const clearEmptyAndDuplicateRules = (stylesheet) => {
  const selectorToIndex = new Map()
  let j = 0
  let len = stylesheet?.cssRules?.length || 0

  const mergeStyleRules = (primaryRule, dupRule) => {
    try {
      const style = dupRule?.style
      if (!style || !primaryRule?.style) return

      for (let k = 0; k < style.length; k++) {
        const propName = style[k]
        if (!propName) continue

        const value = style.getPropertyValue(propName)
        const priority = style.getPropertyPriority(propName)

        primaryRule.style.setProperty(propName, value, priority)
      }
    } catch (e) {
      // ignore merge errors, fallback is to keep both (but we still try to delete dup)
    }
  }

  while (j < len) {
    const rule = stylesheet.cssRules[j]

    if (!rule) {
      j++
      continue
    }

    const selector = rule.selectorText

    if (rule.style && !String(rule.style.cssText || '').trim()) {
      stylesheet.deleteRule(j)
      len--
      continue
    }

    if (typeof selector !== 'string' || !selector.trim()) {
      j++
      continue
    }

    if (!selectorToIndex.has(selector)) {
      selectorToIndex.set(selector, j)
      j++
      continue
    }

    const primaryIndex = selectorToIndex.get(selector)
    const primaryRule = stylesheet.cssRules[primaryIndex]

    if (primaryRule && primaryRule.type === CSSRule.STYLE_RULE && rule.type === CSSRule.STYLE_RULE) {
      mergeStyleRules(primaryRule, rule)
      stylesheet.deleteRule(j)
      len--
      continue
    }

    selectorToIndex.set(selector, j)
    j++
  }
}

const getAffectingClass = (element, property) => {
  let originalClassList = element.classList
  let classList = Array.from(originalClassList)
  const value = getComputedStyle(element).getPropertyValue(property)

  for (let i = 0; i < classList.length; i++) {
    element.className = classList[i]
    let computedStyle = getComputedStyle(element)
    let propValue = computedStyle.getPropertyValue(property)
    if (propValue === value) {
      element.className = classList.join(' ')
      return `.${classList[i]}`
    }
  }
  element.className = classList.join(' ')
  return null
}

const collectCurrentPageSids = () => {
  const sids = [...document.querySelectorAll('[data-sid]')]
    .map(el => el.getAttribute('data-sid'))
  return Array.from(new Set(sids))
}

const collectSids = (sections) => {
  const sids = new Set()

  // add only valid string sids to the Set
  const addSid = (value) => {
    if (typeof value !== 'string') return
    const trimmed = value.trim()
    if (!trimmed) return

    // support multiple sids stored in one attribute/string
    trimmed.split(/\s+/).forEach(v => {
      if (v) sids.add(v)
    })
  }

  function walk (node) {
    if (!node || typeof node !== 'object') return

    // sid can be a string or array on the node
    if (typeof node.sid === 'string') {
      addSid(node.sid)
    } else if (Array.isArray(node.sid)) {
      node.sid.forEach(addSid)
    }

    // sids can also be an array on the node
    if (Array.isArray(node.sids)) {
      node.sids.forEach(addSid)
    }

    // Typography: collect sids from rendered content
    if (['Text', 'Typography'].includes(node.group)) {
      const el = document.querySelector(`[data-uid="${node.uid}"]`)
      if (el) {
        const sidEls = el.matches('[data-sid]')
          ? [el, ...el.querySelectorAll('[data-sid]')]
          : [...el.querySelectorAll('[data-sid]')]

        sidEls
          .map(e => e.getAttribute('data-sid'))
          .forEach(addSid)
      }
    }

    if (Array.isArray(node)) {
      node.forEach(walk)
    } else {
      Object.keys(node).forEach(key => walk(node[key]))
    }
  }

  walk(sections)

  // Set guarantees uniqueness; addSid guarantees strings
  return Array.from(sids)
}

const minifyCss = (css) => {
  return css
    // remove comments
    .replace(/\/\*[\s\S]*?\*\//g, '')
    // remove space around symbols
    .replace(/\s*([{}:;,>])\s*/g, '$1')
    // collapse multiple spaces/newlines into one space
    .replace(/\s+/g, ' ')
    // remove unnecessary semicolons before }
    .replace(/;}/g, '}')
    .trim()
}

const getCssForSids = (sids) => {
  const sheet = getStylesheetEl().sheet
  const sidFragments = sids.map(sid => `--${sid}`)

  // base selectors that should always stay even if they don't contain a SID
  const baseSelectors = [
    '.estage-btn:hover',
    '.estage-btn',
    '.el-block',
    '.el-container',
    '.el-flex-wrapper',
    '.flex-wrapper',
    '.col-hover-v2',
    '.flex-col-inner',
    '.link-active',
    '.el-links-wrapper',
    '.el-menu-link',
    '.el-menu-link:hover'
  ]

  const matchesSelector = (selectorText) => {
    if (!selectorText) return false
    // handle grouped selectors like '.a, .b'
    const selectors = selectorText.split(',').map(s => s.trim())
    return selectors.some(sel => {
      if (baseSelectors.includes(sel)) return true
      return sidFragments.some(f => sel.includes(f))
    })
  }

  // Return ONLY one of these 3 keys for known buckets, otherwise null
  const canonicalBucket = (mt = '') => {
    const norm = normalizeMediaText(mt)

    // Base bucket
    if (!norm || norm === 'all' || norm === 'screen') return 'screen'

    // Breakpoints (even if combined like "screen and (...)" / "all and (...)" )
    if (norm.includes('max-width:1100px')) return '(max-width: 1100px)'
    if (norm.includes('max-width:520px')) return '(max-width: 520px)'

    return null
  }

  // Buckets we always output in this exact order
  const wanted = ['screen', '(max-width: 1100px)', '(max-width: 520px)']

  const bucket = {
    screen: [],
    '(max-width: 1100px)': [],
    '(max-width: 520px)': []
  }

  const bucketSel = {
    screen: new Set(),
    '(max-width: 1100px)': new Set(),
    '(max-width: 520px)': new Set()
  }

  const otherMedia = [] // [{ mediaText, rules: [cssText...] }]
  const otherTop = []

  // Position-based fallback for first 3 media blocks
  const posBuckets = ['screen', '(max-width: 1100px)', '(max-width: 520px)']
  let mediaPos = 0

  const pushToBucket = (key, selectorText, cssText) => {
    if (!key) return
    bucket[key].push(cssText)
    if (selectorText) bucketSel[key].add(selectorText)
  }

  try {
    const rules = [...(sheet?.cssRules || [])]

    for (const rule of rules) {
      switch (rule.type) {
      case CSSRule.STYLE_RULE: {
        if (matchesSelector(rule.selectorText)) {
          // Treat bare style rules as "screen" bucket
          pushToBucket('screen', rule.selectorText, sanitizeNegativeZIndex(rule))
        }
        break
      }

      case CSSRule.MEDIA_RULE: {
        const mt = rule.media?.mediaText || ''

        // IMPORTANT: In the editor, the first 3 @media blocks are always the buckets
        // in this exact order, but their mediaText can be temporarily rewritten for preview.
        // So for the first 3 MEDIA_RULEs, split by position first.
        const posKey = mediaPos < 3 ? posBuckets[mediaPos] : null
        mediaPos++

        const key = posKey || canonicalBucket(mt)

        // Collect matched inner style rules
        const innerCss = []
        const innerSelectors = []

        for (const inner of [...(rule.cssRules || [])]) {
          if (inner?.type !== CSSRule.STYLE_RULE) continue
          if (!matchesSelector(inner.selectorText)) continue
          innerCss.push(sanitizeNegativeZIndex(inner))
          innerSelectors.push(inner.selectorText)
        }

        if (!innerCss.length) break

        if (key) {
          // Put into one of the 3 wanted buckets
          for (let i = 0; i < innerCss.length; i++) {
            pushToBucket(key, innerSelectors[i], innerCss[i])
          }
        } else {
          // Preserve unknown media blocks after the 3 buckets
          otherMedia.push({ mediaText: mt, rules: innerCss })
        }

        break
      }

      case CSSRule.FONT_FACE_RULE:
      case CSSRule.KEYFRAMES_RULE:
        // keep global stuff
        otherTop.push(rule.cssText)
        break

      default:
        break
      }
    }
  } catch (e) {
    console.error('Error while walking CSS rules for SIDs:', e)
  }

  // Ensure base selectors exist in each bucket (like editor expects)
  for (const key of wanted) {
    for (const sel of baseSelectors) {
      if (!bucketSel[key].has(sel)) {
        bucket[key].unshift(`${sel}{}`)
        bucketSel[key].add(sel)
      }
    }
  }

  const mk = (mt, innerArr) => `@media ${mt}{${innerArr.join('')}}`

  const out =
    mk('screen', bucket['screen']) +
    mk('(max-width: 1100px)', bucket['(max-width: 1100px)']) +
    mk('(max-width: 520px)', bucket['(max-width: 520px)']) +
    otherMedia.map(m => mk(m.mediaText, m.rules)).join('') +
    otherTop.join('')

  return minifyCss(out)
}

const applyPageStyles = (styles, selector = 'sty') => {
  if (!styles) return

  const $styEl = document.getElementById(selector)
  if (!$styEl) return

  // base selectors that should always exist in each breakpoint bucket
  const baseSelectors = [
    '.estage-btn:hover',
    '.estage-btn',
    '.el-block',
    '.el-container',
    '.el-flex-wrapper',
    '.flex-wrapper',
    '.col-hover-v2',
    '.flex-col-inner',
    '.link-active',
    '.el-links-wrapper',
    '.el-menu-link',
    '.el-menu-link:hover'
  ]

  const wantedMedia = ['screen', '(max-width: 1100px)', '(max-width: 520px)']

  const normalizeToMediaBuckets = (cssText) => {
    const $tmp = document.createElement('style')
    document.head.appendChild($tmp)
    $tmp.textContent = cssText || ''

    const bucketInner = ['', '', '']
    const otherMedia = []
    const otherTop = []

    let mediaPos = 0

    try {
      const rules = [...($tmp.sheet?.cssRules || [])]

      for (const r of rules) {
        if (!r) continue

        if (r.type === CSSRule.STYLE_RULE) {
          bucketInner[0] += r.cssText
          continue
        }

        if (r.type === CSSRule.MEDIA_RULE) {
          const innerText = [...(r.cssRules || [])].map(ir => ir.cssText).join('')

          if (mediaPos < 3) {
            bucketInner[mediaPos] += innerText
            mediaPos++
          } else {
            otherMedia.push(r.cssText)
          }

          continue
        }

        otherTop.push(r.cssText)
      }
    } catch (e) {
      $tmp.remove()
      return cssText
    }

    $tmp.remove()

    const mk = (mt, inner) => `@media ${mt}{${inner || ''}}`

    return (
      mk(wantedMedia[0], bucketInner[0]) +
      mk(wantedMedia[1], bucketInner[1]) +
      mk(wantedMedia[2], bucketInner[2]) +
      otherMedia.join('') +
      otherTop.join('')
    )
  }

  $styEl.textContent = normalizeToMediaBuckets(styles)

  const sheet = $styEl.sheet
  if (!sheet || !sheet.cssRules) return

  const ensureMediaRuleAt = (mediaText, atIndex) => {
    const rules = [...sheet.cssRules]
    const wantedNorm = normalizeMediaText(mediaText)
    const existingIndex = rules.findIndex(r =>
      r.type === CSSRule.MEDIA_RULE && normalizeMediaText(r.media?.mediaText) === wantedNorm
    )

    // If missing, insert an empty @media at the right spot.
    if (existingIndex === -1) {
      try {
        sheet.insertRule(`@media ${mediaText}{}`, Math.min(atIndex, sheet.cssRules.length))
      } catch (e) {
        console.error('applyPageStyles: failed to insert media rule', mediaText, e)
      }
      return sheet.cssRules[atIndex]
    }

    // If present but not at the desired index, move it (keeping its inner rules).
    if (existingIndex !== atIndex) {
      const existingRule = sheet.cssRules[existingIndex]
      const inner = existingRule.type === CSSRule.MEDIA_RULE
        ? [...existingRule.cssRules].map(r => r.cssText).join('')
        : ''

      try {
        sheet.deleteRule(existingIndex)
      } catch (e) {
        // ignore
      }

      try {
        sheet.insertRule(`@media ${mediaText}{${inner}}`, Math.min(atIndex, sheet.cssRules.length))
      } catch (e) {
        console.error('applyPageStyles: failed to move media rule', mediaText, e)
      }
    }

    return sheet.cssRules[atIndex]
  }

  const mediaRules = wantedMedia
    .map((mt, idx) => ensureMediaRuleAt(mt, idx))
    .filter(Boolean)

  const ensureBaseSelectors = (mediaRule) => {
    if (!mediaRule || mediaRule.type !== CSSRule.MEDIA_RULE) return

    baseSelectors.forEach(sel => {
      const has = [...mediaRule.cssRules].some(r => r.selectorText === sel)
      if (!has) {
        try {
          mediaRule.insertRule(`${sel}{}`, 0)
        } catch (e) {
          // ignore invalid selector errors
        }
      }
    })
  }

  mediaRules.forEach(ensureBaseSelectors)
}

const checkCSSMedia = (cssText = '') => {
  const wantedMedia = ['screen', '(max-width: 1100px)', '(max-width: 520px)']

  if (!cssText || typeof cssText !== 'string') return ''

  const $tmp = document.createElement('style')
  document.head.appendChild($tmp)
  $tmp.textContent = cssText

  const bucketInner = ['', '', ''] // inner cssText for wantedMedia[0..2]
  const otherMedia = [] // full cssText of other @media blocks
  const otherTop = [] // @font-face, @keyframes, etc.

  let mediaPos = 0

  try {
    const rules = [...($tmp.sheet?.cssRules || [])]

    for (const r of rules) {
      if (!r) continue

      // Bare style rules should behave like screen bucket
      if (r.type === CSSRule.STYLE_RULE) {
        bucketInner[0] += r.cssText
        continue
      }

      if (r.type === CSSRule.MEDIA_RULE) {
        // NOTE: we intentionally map by POSITION for the first 3 media rules.
        // This fixes cases where mediaText is rewritten to `screen` for preview.
        const innerText = [...(r.cssRules || [])].map(ir => ir.cssText).join('')

        if (mediaPos < 3) {
          bucketInner[mediaPos] += innerText
          mediaPos++
        } else {
          otherMedia.push(r.cssText)
        }

        continue
      }

      // Keep global rules outside media buckets.
      otherTop.push(r.cssText)
    }
  } catch (e) {
    // If parsing fails, return original css (minified) so we don't break exports.
    $tmp.remove()
    return minifyCss(cssText)
  }

  $tmp.remove()

  const mk = (mt, inner) => `@media ${mt}{${inner || ''}}`

  // Always output the 3 canonical buckets first, in exact order.
  const normalized =
    mk(wantedMedia[0], bucketInner[0]) +
    mk(wantedMedia[1], bucketInner[1]) +
    mk(wantedMedia[2], bucketInner[2]) +
    otherMedia.join('') +
    otherTop.join('')

  return minifyCss(normalized)
}

// const applyPageStyles = (styles, selector = 'sty') => {
//   if (!styles) return
//   const $styEl = document.getElementById(selector)
//   $styEl.textContent = styles
//
//   const styleRules = [...$styEl.sheet.cssRules]
//
//   styleRules.forEach((rule) => {
//     const selectors = [
//       '.estage-btn:hover',
//       '.estage-btn',
//       '.el-block',
//       '.el-container',
//       '.el-flex-wrapper',
//       '.flex-wrapper',
//       '.col-hover-v2',
//       '.flex-col-inner',
//       '.link-active',
//       '.el-links-wrapper',
//       '.el-menu-link',
//       '.el-menu-link:hover'
//     ]
//
//     selectors.forEach(selector => {
//       if (![...rule.cssRules].filter(r => r.selectorText === selector).length) {
//         rule.insertRule(`${selector}{}`, 0)
//       }
//     })
//   })
// }

const getUsedFonts = (prefix = false) => {
  const styleRules = [...getStylesheetEl().sheet.cssRules]
  const usedFonts = []

  styleRules.forEach((rule) => {
    if (!rule || rule.type !== CSSRule.MEDIA_RULE) return
    let i = 0, len = [...rule.cssRules].length
    while (i < len) {
      const font = rule.cssRules[i].style.getPropertyValue('font-family').replaceAll('"', '')
      if (font && usedFonts.indexOf(font) === -1 && (prefix ? rule.cssRules[i].selectorText.indexOf(prefix) === 0 : true)) {
        usedFonts.push(font)
      }
      i++
    }
  })

  return usedFonts
}

/** remove quotes and normalize spacing/case */
const normalizeFamilyName = (name) => {
  return name
    .trim()
    .replace(/^['"]|['"]$/g, '')
    .replace(/\s+/g, ' ')
    .toLowerCase()
}

/**
 * Extract all font-family names referenced in CSS.
 * Handles:
 *  - font-family: A, B, sans-serif;
 *  - font: italic 700 16px/1.2 "A", sans-serif;
 */
const extractUsedFontFamilies = (pageCss) => {
  const used = new Set()
  if (!pageCss || typeof pageCss !== 'string') return used

  const addFamiliesFromValue = (value) => {
    if (!value) return
    value
      .replace(/!important\s*$/i, '')
      .split(',')
      .forEach(part => {
        const fam = normalizeFamilyName(part)
        if (fam && !fam.startsWith('var(')) used.add(fam)
      })
  }

  // 1) font-family + custom props like --something-font-family:
  const ffRegex = /(font-family|--[^:;{}]*font-family)\s*:\s*([^;}]*)/gi
  let m
  while ((m = ffRegex.exec(pageCss))) {
    addFamiliesFromValue(m[2])
  }

  // 2) font shorthand + custom props like --font:
  const fontRegex = /(font|--[^:;{}]*font)\s*:\s*([^;}]*)/gi
  while ((m = fontRegex.exec(pageCss))) {
    const raw = (m[2] || '').trim()
    if (!raw) continue

    // remove trailing !important if present
    const cleaned = raw.replace(/!important\s*$/i, '').trim()

    // Try to take everything after the size[/line-height] token as family list.
    // Example: italic 700 16px/1.2 "Open Sans", sans-serif
    const sizeMatch = cleaned.match(/\b\d+(?:\.\d+)?(?:px|pt|em|rem|%|vh|vw)\b(?:\s*\/\s*[^ ,]+)?/i)
    const afterSize = sizeMatch
      ? cleaned.slice(cleaned.indexOf(sizeMatch[0]) + sizeMatch[0].length).trim()
      : cleaned

    addFamiliesFromValue(afterSize)
  }

  // 3) Resolve simple CSS variable usages in font declarations.
  // If pageCss has `font-family: var(--ff)` and later `--ff: "Nunito"` we include Nunito.
  const varUseRegex = /var\(\s*(--[A-Za-z0-9_-]+)\s*\)/g
  const varsUsed = new Set()
  while ((m = varUseRegex.exec(pageCss))) {
    varsUsed.add(m[1])
  }

  if (varsUsed.size) {
    varsUsed.forEach(varName => {
      const defRegex = new RegExp(`${varName}\\s*:\\s*([^;}]*)`, 'gi')
      let dm
      while ((dm = defRegex.exec(pageCss))) {
        addFamiliesFromValue(dm[1])
      }
    })
  }

  // 4) Inline styles in DOM (covers custom tags like <spanec style="font-family:Gotham">)
  if (typeof document !== 'undefined') {
    try {
      document.querySelectorAll('[style]').forEach(el => {
        const inlineFf = el.style?.getPropertyValue?.('font-family')
        if (inlineFf) addFamiliesFromValue(inlineFf)
      })
    } catch (e) {
      // ignore DOM access errors
    }
  }

  return used
}

/** Extract @font-face blocks and keep only those whose family is used. */
const filterFontFacesByUsage = (pageCss, fontsCss) => {
  const usedFamilies = extractUsedFontFamilies(pageCss)

  // Remove commas that separate font-face blocks in already-minified/bad CSS:
  // "...},@font-face{...}" -> "...}@font-face{...}"
  const cleanedFontsCss = fontsCss.replace(/}\s*,\s*(?=@font-face)/g, '}')

  // match each @font-face {...} (non-greedy, across newlines)
  const faceBlocks = cleanedFontsCss.match(/@font-face\s*{[\s\S]*?}/gim) || []

  const kept = faceBlocks.filter(block => {
    const famMatch = block.match(/font-family\s*:\s*([^;}]*)/i)
    if (!famMatch) return false

    const famNorm = normalizeFamilyName(famMatch[1])
    return usedFamilies.has(famNorm)
  })

  return {
    usedFamilies,
    keptFontFaces: minifyCss(kept.join('\n'))
  }
}

const insertSectionStyle = (styles) => {
  const $styEl = getStylesheetEl()

  styles.forEach((css, index) => {
    css.forEach(c => {
      const mediaRule = $styEl.sheet.cssRules[index]
      if (!mediaRule || mediaRule.type !== CSSRule.MEDIA_RULE) return
      mediaRule.insertRule(c, mediaRule.cssRules.length)
    })
  })
}

const getStylesBySid = (sids) => {
  const arr = [
    [],
    [],
    []
  ]
  const styleRules = [...getStylesheetEl().sheet.cssRules]

  styleRules.forEach((rule, index) => {
    if (!rule || rule.type !== CSSRule.MEDIA_RULE) return
    let j = 0, len = [...rule.cssRules].length
    while (j < len) {
      sids.forEach(sid => {
        if (rule.cssRules[j].selectorText.indexOf(sid) !== -1) {
          arr[index].push(rule.cssRules[j].cssText)
        }
      })

      j++
    }
  })

  return arr
}

// const getRelatedToStyleSids = (sids) => {
//   const arr = []
//
//   const styleRules = [...document.getElementById('sty').sheet.cssRules]
//
//   styleRules.forEach((rule) => {
//     let j = 0, len = [...rule.cssRules].length
//     while (j < len) {
//       sids.forEach(sid => {
//         if (rule.cssRules[j].selectorText.indexOf(sid) !== -1 && arr.indexOf(sid) === -1) {
//           arr.push(rule.cssRules[j].cssText)
//         }
//       })
//
//       j++
//     }
//   })
//
//   return arr
// }

const combineStyles = (...styles) => {
  // Flatten arguments, allow passing arrays or objects with a `css` property
  const cssParts = styles
    .flat()
    .filter(Boolean)
    .map(s => {
      if (typeof s === 'string') return s
      if (s && typeof s === 'object' && typeof s.css === 'string') return s.css
      return ''
    })
    .filter(Boolean)

  if (!cssParts.length) return ''

  // Create a temporary style element to leverage the CSSOM
  const $style = document.createElement('style')
  document.head.appendChild($style)
  $style.textContent = cssParts.join(' ')

  const sheet = $style.sheet
  if (!sheet || !sheet.cssRules) {
    $style.remove()
    return minifyCss(cssParts.join(' '))
  }

  // Merge @media blocks with the same mediaText
  const mediaMap = new Map()
  let i = 0

  while (i < sheet.cssRules.length) {
    const rule = sheet.cssRules[i]

    if (rule.type === CSSRule.MEDIA_RULE) {
      const key = rule.media && rule.media.mediaText ? rule.media.mediaText : ''

      if (!mediaMap.has(key)) {
        // First @media of this type – remember and clean duplicates inside it
        mediaMap.set(key, rule)
        clearEmptyAndDuplicateRules(rule)
        i++
      } else {
        // Merge this @media into the primary one
        const primary = mediaMap.get(key)

        try {
          const inner = rule.cssRules
          while (inner.length) {
            const innerRuleText = inner[0].cssText
            try {
              primary.insertRule(innerRuleText, primary.cssRules.length)
            } catch (e) {
              console.error('combineStyles: failed to merge inner rule', innerRuleText, e)
            }
            rule.deleteRule(0)
          }
        } catch (e) {
          console.error('combineStyles: error while merging @media blocks', e)
        }

        // Remove the now-empty duplicate @media block
        try {
          sheet.deleteRule(i)
        } catch (e) {
          console.error('combineStyles: failed to delete merged @media rule', e)
          i++
        }
      }
    } else {
      i++
    }
  }

  // Final pass: clean duplicates/empties inside each remaining @media block
  for (let j = 0; j < sheet.cssRules.length; j++) {
    const r = sheet.cssRules[j]
    if (r.type === CSSRule.MEDIA_RULE) {
      clearEmptyAndDuplicateRules(r)
    }
  }

  const combined = [...sheet.cssRules].map(r => r.cssText).join('')
  $style.remove()
  return minifyCss(combined)
}

const clonePart = (json, node) => {
  const sids = Array.from(new Set([...node.querySelectorAll('[data-sid]')].map(node => node.getAttribute('data-sid'))))
  let jsonString = JSON.stringify({
    ...json,
    css: getStylesBySid(sids),
    sids
  })
  //
  // sids.forEach(sid => {
  //   const newSid = shortid.generate()
  //   jsonString = jsonString.replaceAll(sid, newSid)
  // })

  return jsonString
}

const debug = (json, node) => {
  const sids = Array.from(new Set([...node.querySelectorAll('[data-sid]')].map(node => node.getAttribute('data-sid'))))

  console.log({
    content: json.options.content,
    css: `this.css = '${getStylesBySid(sids)}'`,
    sids: `this.sids = [${sids}]`,
    sid: `this.sid = '${json.sid}'`
  })
}

const generateSID = (node, hash, sid = null) => {
  return isGlobalPreset(node) ? `global--${sid || shortid.generate()}` : `l${hash}--${sid || shortid.generate()}`
}

const isGlobalSid = (sid) => {
  return sid.indexOf('global') !== -1
}

const isGlobalPreset = (node) => {
  if (!node) return false
  return node.closest('.global-block')
}

const isChangedPreset = (node, sid) => {
  if (isGlobalPreset(node)) {
    return !isGlobalSid(sid)
  }

  return isGlobalSid(sid)
}

const renameSid = (oldSid, newSid) => {
  const styleRules = [...getStylesheetEl().sheet.cssRules]

  styleRules.forEach(rule => {
    if (!rule || rule.type !== CSSRule.MEDIA_RULE) return
    let j = 0, len = [...rule.cssRules].length
    while (j < len) {
      if (rule.cssRules[j].selectorText.indexOf(oldSid) !== -1) {
        rule.cssRules[j].selectorText = rule.cssRules[j].selectorText.replaceAll(oldSid, newSid)
      }

      j++
    }
  })
}

const duplicateStyles = (oldSid, newSid) => {
  const css = JSON.parse(JSON.stringify(getStylesBySid([oldSid])).replaceAll(oldSid, newSid))
  insertSectionStyle(css)
}

const convertStyles = (oldSid, newSid) => {
  if (document.querySelectorAll(`[data-sid="${oldSid}"]`).length === 1) {
    return renameSid(oldSid, newSid)
  }

  duplicateStyles(oldSid, newSid)
}

const getLogicalValueFromComputed = (el, prop) => {
  const cs = window.getComputedStyle(el)

  switch (prop) {
  case 'line-height': {
    const lh = cs.lineHeight
    const fs = cs.fontSize

    if (lh && !lh.endsWith('px')) return lh

    const lhPx = parseFloat(lh)
    const fsPx = parseFloat(fs)
    if (!lhPx || !fsPx) return lh

    const ratio = lhPx / fsPx
    return String(+ratio.toFixed(3))
  }

  // case 'font-weight': {
  //   const fw = cs.fontWeight
  //   if (fw === '400') return 'normal'
  //   if (fw === '700') return 'bold'
  //   return fw
  // }

  case 'letter-spacing': {
    const ls = cs.letterSpacing

    if (!ls) return ls

    if (ls === 'normal') return '0'

    const num = parseFloat(ls)
    if (!isNaN(num) && ls.endsWith('px')) {
      return String(+num.toFixed(3))
    }

    return ls
  }

  default:
    return cs.getPropertyValue(prop)
  }
}

const getSidStyles = (sid) => {
  return [...getStylesheetEl().sheet.cssRules].filter(_s => {
    return _s.selectorText === sid
  })
}

const getSidStyle = (sid, rule, presudo = null) => {
  const rules = getSidStyles(sid)

  let style = ''
  let j = 0
  const len = rules.length

  while (j < len) {
    if (rules[j].selectorText === sid && rules[j].style[rule]) {
      return style = rules[j].style[rule]
    }

    j++
  }

  if (!style) {
    if (rule === 'position') style = 'static'

    const $el = document.querySelector(sid)
    if (!$el) return ''

    style = getLogicalValueFromComputed($el, rule, presudo)
  }

  return style
}

const addSidRules = (rules, options = {}) => {
  const varName = typeof options === 'string' ? options : (options && options.varName ? options.varName : null)
  rules.forEach((rule) => {
    let j = 1,
      selector = rule[0],
      cleanSelector = selector.replace(/::after|::before|:hover/, ''),
      propStr = ''
    if (Array.isArray(rule[1][0])) {
      rule = rule[1]
      j = 0
    }
    let pl = rule.length

    while (j < pl) {
      const prop = rule[j]

      if (getSidStyles(cleanSelector).length) {
        const styleObject = getSidStyles(cleanSelector)[0].style

        if (prop[1] !== '') {
          styleObject.setProperty(prop[0], prop[1])

          if (varName) {
            styleObject.setProperty(varName, prop[1])
          }
        } else {
          styleObject.removeProperty(prop[0])

          if (varName) {
            styleObject.removeProperty(varName)
          }
        }
        return
      }

      getSidStyles(cleanSelector).forEach(_s => {
        propStr += _s.style.cssText
      })

      propStr += `${prop[0]}: ${prop[1]}${prop[2] ? ' !important' : ''};\n`

      j++
    }

    getStylesheetEl().sheet.insertRule(`${selector}{${propStr}}`, getStylesheetEl().sheet.cssRules.length)
  })
}

const parseCssLength = (input) => {
  const str = String(input ?? '').trim()
  if (!str) return { value: null, unit: null }

  // Common non-numeric keywords for size-like properties
  const keywordUnits = ['auto', 'inherit', 'initial', 'unset', 'normal', 'fit-content']
  if (keywordUnits.includes(str)) {
    return { value: null, unit: str }
  }

  // Match number + optional unit (length/percentage-ish)
  const match = str.match(/^(-?(?:\d+|\d*\.\d+))\s*(px|em|rem|%|vh|vw|vmin|vmax|ch|ex|cm|mm|in|pt|pc)?$/i)
  if (!match) {
    return { value: null, unit: null }
  }

  return {
    value: parseFloat(match[1]),
    unit: (match[2] || '').toLowerCase() // unitless (e.g., line-height: 1.5)
  }
}

const splitRuleValue = (str) => {
  const { value } = parseCssLength(str)
  return value || ''
}

const splitRuleUnit = (str) => {
  const { unit } = parseCssLength(str)
  return unit || ''
}

export {
  pageStylesToString,
  pageStylesToStringAsync,
  getOriginalComponentPropertyValue,
  getAffectingClass,
  applyPageStyles,
  getPageStyles,
  insertSectionStyle,
  clonePart,
  getUsedFonts,
  filterFontFacesByUsage,
  deleteStylesBySid,
  getStylesBySid,
  generateSID,
  isGlobalSid,
  isGlobalPreset,
  isChangedPreset,
  convertStyles,
  debug,
  getStylesheetEl,
  addSidRules,
  getSidStyle,
  getSidStyles,
  retriveStyles,
  retriveAllStyles,
  getVarValue,
  collectCurrentPageSids,
  collectSids,
  getCssForSids,
  combineStyles,
  minifyCss,
  parseCssLength,
  splitRuleValue,
  splitRuleUnit,
  checkCSSMedia
}

window.pageStylesToString = pageStylesToString
window.origPageStylesToString = origPageStylesToString
